import { Component, ElementRef, OnInit, QueryList, ViewChild, ViewChildren, ViewEncapsulation } from '@angular/core';
import { AbstractControl, FormArray, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { config } from '../../../../../config/config';
import { HubFacebookFeedsCommand } from '../../../../commands/hub/event/add.facebook.general.feed.command';
import { CreateEventCommand } from '../../../../commands/hub/event/create.event.command';
import { VisitorSettingsCommand } from '../../../../commands/pagination.query.command';
import { DateTimeInputComponent } from '../../../../common/components/datetime.input/datetime.input.component';
import { ButtonTriggerEvent } from '../../../../common/components';
import { BaseComponent } from '../../../../common/components/base.component';
import { SuccessWrapComponent } from '../../../../common/components/success.wrap/success.wrap.component';
import { localStorageKeys } from '../../../../common/constants/local.storage.keys';
import { clone, isNullOrUndefined } from '../../../../common/extensions';
import { CanComponentDeactivate } from '../../../../common/guards/index';
import { ModalService } from '../../../../common/services/modal.service';
import { HubVisitorFeatureToggle } from '../../../../models/hub/hub.visitor.feature.toggle';
import { AlertifyService } from '../../../../services/alertify.service';
import { EventTimeService } from '../../../../services/event.time.service';
import { HubApiService } from '../../../../services/hub.api.service';
import { ImageService } from '../../../../services/image.service';
import { HubFacebookPageFeedsStore, ToggleStore } from '../../../../stores/hub.calendar.store';
import { HubComponent } from '../../hub.component';
import { VisitorSettingsStore } from '../../../../stores/visitor.calendar.store';

@Component({
  selector: 'ig-hub-create-event',
  templateUrl: './create.event.component.html',
  styleUrls: ['./create.event.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class CreateEventComponent extends BaseComponent implements OnInit, CanComponentDeactivate {
  public command = new CreateEventCommand();
  public time = '00';
  public timezoneAddress: any;
  public timezoneLabel = '';
  public createEventForm: FormGroup;
  public hubName: string;
  public toggles = new HubVisitorFeatureToggle();
  public manualSetTimezone = false;
  public showTimezoneCitySelector = false;
  public isLoadingTimezone = false;
  public lastTimezone = null;
  public frequencyItems = [];
  public frequencyEventTimes: any[] = [];
  public totalTimes = '';
  public dayInWeek = -1;
  public hasSetTimezone = false;
  public weekNo = -1;
  public isImageChanged = false;
  public hasCopyrights = false;
  public visitorSettings = new VisitorSettingsCommand();
  public isAllowOnlineEvents = true;
  public resizeImageTemp: string | null = null;
  public imageUploaderLabel2 = 'Please upload images in JPG, PNG or WEBP format. Landscape images recommended';
  @ViewChild('description') public descriptionArea: ElementRef;
  @ViewChild('createEventSuccess') public successWrap: SuccessWrapComponent;
  @ViewChildren('dateTimeInput') public dateTimeInput: QueryList<DateTimeInputComponent>;

  public get timeRangeControls(): AbstractControl[] {
    return (<FormArray>this.createEventForm.get('timeRanges')).controls;
  }

  constructor(
    private formBuilder: FormBuilder,
    private activatedRoute: ActivatedRoute,
    private modalService: ModalService,
    private imageService: ImageService,
    private parent: HubComponent,
    private togglesStore: ToggleStore,
    private visitorSettingsCommand: VisitorSettingsStore,
    private alertify: AlertifyService,
    private hubFacebookFeedsStore: HubFacebookPageFeedsStore,
    private hubApiService: HubApiService
  ) {
    super({ activatedRoute });
  }

  public ngOnInit() {
    this.hubName = this.allRouteParams.hubName;
    this.command.defaultImage = config.defaultImageUrl;
    this.createForm();
    this.command.frequency = 'custom';
    this.resetFrequencyDropdown();
    // this.createEventForm.get('facebookEvent')
    //     .valueChanges.subscribe((value: boolean) => {
    //     if (value) {
    //         this.createEventForm.get('hostLink').setValidators(Validators.required);
    //         this.createEventForm.get('eventLink').setValidators(Validators.required);
    //     } else {
    //         this.createEventForm.get('hostLink').clearValidators();
    //         this.createEventForm.get('eventLink').clearValidators()
    //     }
    // });
    this.addTimeRange();

    this.setDefaultTimeValue();
    this.resetTimezoneLabel();
    // this.setImageUploaderLabel2();

    this.visitorSettingsCommand.settingsCommand
      .takeUntil(this.isDestroyed)
      .subscribe((settingsCommand: VisitorSettingsCommand) => {
        this.visitorSettings = settingsCommand;
        this.isAllowOnlineEvents = this.visitorSettings.isAllowOnlineEvents;
      });

    this.togglesStore.toggleCommand.takeUntil(this.isDestroyed).subscribe((toggleCommand: HubVisitorFeatureToggle) => {
      this.toggles =
        isNullOrUndefined(toggleCommand) || isNullOrUndefined(toggleCommand.placeCalendar)
          ? new HubVisitorFeatureToggle()
          : toggleCommand;
      if (this.toggles.manualEvents === false) {
        for (let i in this.createEventForm.controls) {
          let control = this.createEventForm.controls[i];
          control.disable();
        }
      }
    });
  }

  public get addressInputLabel() {
    if (!this.isAllowOnlineEvents) {
      return `Location / Address *`;
    }
    return `Location / Address ${this.command.isOnlineEvent ? '' : '*'} (or check Virtual Events)`;
  }

  public virtualEventChange() {
    if (this.command.isOnlineEvent) {
      this.command.isHybridEvent = false;
    }
  }

  public hybridEventChange() {
    if (this.command.isHybridEvent) {
      this.command.isOnlineEvent = false;
    }
  }

  public setFrequencyDropdown() {
    if (this.command.eventTimes.length > 0) {
      this.setDefaultTimeValue();
    }

    this.frequencyItems = this.frequencyItems.filter((x) => x.value !== 'last');

    let start = this.command.eventTimes[0].startTime;
    this.dayInWeek = EventTimeService.getDayOfWeek(start, this.command.timezone);
    let dayText = config.dayOfWeek[this.dayInWeek].name;

    let weekly = this.frequencyItems.first((x) => x.value === 'weekly');
    if (weekly) {
      weekly.text = `Weekly on ${dayText}`;
    }

    let biWeekly = this.frequencyItems.first((x) => x.value === 'biweekly');
    if (biWeekly) {
      biWeekly.text = `Every Other ${dayText} (starts from ${EventTimeService.getDayAndMonth(
        start,
        this.command.timezone
      )})`;
    }

    let monthly = this.frequencyItems.first((x) => x.value === 'monthly');
    if (monthly) {
      let no = EventTimeService.getWeekDayNo(start, this.command.timezone);
      if (!no.isLast) {
        this.weekNo = no.no;
        monthly.text = `Monthly on the ${no.text} ${dayText}`;
        monthly.value = 'monthly';
      } else if (no.isLast && no.text === 'last') {
        monthly.text = `Monthly on the last ${dayText}`;
        monthly.value = 'last';
      } else if (no.isLast && no.text !== 'last') {
        this.weekNo = no.no;
        monthly.text = `Monthly on the ${no.text} ${dayText}`;
        monthly.value = 'monthly';

        this.frequencyItems.push({
          value: 'last',
          text: `Monthly on the last ${dayText}`,
        });
      }
    }
  }

  public frequencySourceChange(target) {
    this.command.frequency = target.value;
    let start = this.command.eventTimes[0].startTime;
    let end = this.command.eventTimes[0].endTime;
    // end = EventTimeService.addHours(new Date(end), -1);
    let eventTimes: any[] = [],
      days = [];
    let diff = 0;
    let scrossDaylight = '';

    switch (target.value) {
      case 'daily':
        diff = EventTimeService.diff(start, end);
        this.totalTimes = `${diff + 1} Dates ${EventTimeService.getDayAndMonth(
          start,
          this.command.timezone
        )} - ${EventTimeService.getDayAndMonth(end, this.command.timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = EventTimeService.addHours(new Date(start), 24 * i);

          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setEventTimes(time.startTime, time.endTime, eventTimes);
          scrossDaylight = time.scrossDaylight;
        }

        if (scrossDaylight === 'ST') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, -1);
          }
        }
        if (scrossDaylight === 'DT') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, 1);
          }
        }
        break;
      case 'weekly':
        diff = EventTimeService.diff(start, end, 'weeks');
        this.totalTimes = `${diff + 1} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.command.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.command.timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = EventTimeService.addHours(new Date(start), 24 * 7 * i);
          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setEventTimes(time.startTime, time.endTime, eventTimes);
          scrossDaylight = time.scrossDaylight;
        }

        if (scrossDaylight === 'ST') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, -1);
          }
        }
        if (scrossDaylight === 'DT') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, 1);
          }
        }

        break;
      case 'biweekly':
        diff = EventTimeService.diff(start, end, 'biweekly');
        this.totalTimes = `${diff + 1} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.command.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.command.timezone)}`;

        for (let i = 0; i <= diff; i++) {
          let startTime = EventTimeService.addHours(new Date(start), 24 * 14 * i);
          let time = this.adjustForDaylightSaving(start, end, startTime);
          this.setEventTimes(time.startTime, time.endTime, eventTimes);
          scrossDaylight = time.scrossDaylight;
        }

        if (scrossDaylight === 'ST') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, -1);
          }
        }
        if (scrossDaylight === 'DT') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, 1);
          }
        }
        break;
      case 'monthly':
        days = EventTimeService.getSameDayInWeek(start, end, this.dayInWeek, this.weekNo, this.command.timezone);
        this.totalTimes = `${days.length} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.command.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.command.timezone)}`;
        days.forEach((x) => {
          let time = this.adjustForDaylightSaving(start, end, x);
          this.setEventTimes(time.startTime, time.endTime, eventTimes);
          scrossDaylight = time.scrossDaylight;
        });

        if (scrossDaylight === 'ST') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, -1);
          }
        }
        if (scrossDaylight === 'DT') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, 1);
          }
        }
        break;
      case 'last':
        days = EventTimeService.getLastWeekDays(start, end, this.dayInWeek, this.command.timezone);
        this.totalTimes = `${days.length} Dates ${EventTimeService.getDayAndMonthAndYear(
          start,
          this.command.timezone
        )} - ${EventTimeService.getDayAndMonthAndYear(end, this.command.timezone)}`;
        days.forEach((x) => {
          let time = this.adjustForDaylightSaving(start, end, x);
          this.setEventTimes(time.startTime, time.endTime, eventTimes);
          scrossDaylight = time.scrossDaylight;
        });

        if (scrossDaylight === 'ST') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, -1);
          }
        }
        if (scrossDaylight === 'DT') {
          for (const time of eventTimes) {
            time.endTime = EventTimeService.addHours(time.endTime, 1);
          }
        }
        break;
      default:
        break;
    }
    this.frequencyEventTimes = eventTimes;
  }

  private createForm() {
    this.createEventForm = this.formBuilder.group({
      name: [null, [Validators.required]],
      description: [null, []],
      // timeRanges: this.formBuilder.array([]),
      address: [null, []],
      host: [null, [Validators.required]],
      category: [null, []],
      ticketUri: [null, []],
      facebookEvent: [null, []],
      hostLink: [null, []],
      eventLink: [null, []],
      imageLink: [null, []],
      timezone: [null, []],
      venue: [null, []],
      organizerEmail: [null, []],
    });
  }

  public get isFormInvalid() {
    return (
      this.createEventForm.invalid ||
      (!this.command.isOnlineEvent && !this.command.address) ||
      (this.isImageChanged && !this.hasCopyrights)
    );
  }

  public async createEvent(event: ButtonTriggerEvent) {
    // if (!this.hasSetTimezone) {
    if (!this.command.timezone) {
      this.alertify.warning('Please set the timezone');
      event.complete();
      return;
    }
    let { hubName } = this.allRouteParams;
    this.command.isHubCreated = true;

    // if (this.command.image) {
    //     let imageFile = await this.imageService.compressImageFile(this.command.image);
    //     if (imageFile) {
    //         this.command.image = imageFile;
    //     }
    // }

    // image uploaded
    if (this.resizeImageTemp) {
      const res = await this.hubApiService.uploadBase64ImageToAWS(this.resizeImageTemp);
      if (res.success) {
        this.command.image = null;
        this.command.imageLink = res.url;
      }
    }

    if (
      this.command.address &&
      this.command.address.address_components &&
      this.command.address.address_components.length > 0
    ) {
      this.command.address.addressComponents = clone(this.command.address.address_components);
      delete this.command.address.address_components;
    }

    this.hubApiService
      .createEvent(hubName, this.command, this.frequencyEventTimes)
      .finally(event.complete)
      .subscribe(() => {
        this.alertify.successfully('Create event');
        this.command = new CreateEventCommand();
        this.command.frequency = 'custom';
        this.resetFrequencyDropdown();
        this.createForm();
        this.addTimeRange();
        this.setDefaultTimeValue();
        this.lastTimezone ? (this.command.timezone = this.lastTimezone) : this.resetTimezoneLabel();
        this.descriptionArea.nativeElement.style.height = '40px';
        this.resizeImageTemp = null;

        this.hubApiService.getPageFeeds(this.hubName).subscribe((data) => {
          let pageCommand = new HubFacebookFeedsCommand(data);
          this.hubFacebookFeedsStore.pagesCommand.next(pageCommand);
        });
      });
  }

  public async onImageChange(file) {
    if (file) {
      this.resizeImageTemp = await this.imageService.compressImageFileToBase64(file, config.compressImgWidth);
      // console.log('resizeImageTemp 1', this.resizeImageTemp.length);
      // // do not allow over 1MB
      // if (this.resizeImageTemp.length > 1000000) {
      //   this.resizeImageTemp = await this.imageService.compressImageFileToBase64(file, 600);
      //   console.log('resizeImageTemp 2', this.resizeImageTemp.length);
      // }
      this.isImageChanged = true;
    }
  }

  public imageResized(imgBase64: string) {
    this.resizeImageTemp = imgBase64;
  }

  public setEventTime(time, timeRange) {
    if (time.startTime) {
      timeRange.startTime = time.startTime;
    }
    if (time.endTime) {
      timeRange.endTime = time.endTime;
    }
    this.frequencySourceChange({ value: this.command.frequency });
  }

  public showTimezoneCity() {
    if (this.toggles.manualEvents) {
      this.showTimezoneCitySelector = true;
    }
  }

  public addTimeRange() {
    this.command.eventTimes.push({
      //startTime: new Date().setDate(new Date().getDate() + 1),
      //endTime: new Date().setDate(new Date().getDate() + 1)
    });

    // let timeRangeGroups = (<FormArray>this.createEventForm.get('timeRanges')).controls;
    // let getTimeRangeFunc = () => {
    //     let group = timeRangeGroups[this.command.eventTimes.length - 1];
    //     return group ? {start: group.get('startTime').value, end: group.get('endTime').value} : null;
    // };
    // let getOtherTimeRangesFunc = () => {
    //     let otherGroups = timeRangeGroups.slice(0, this.command.eventTimes.length - 1);
    //     return otherGroups.map(group => {
    //         return {start: group.get('startTime').value, end: group.get('endTime').value};
    //     });
    // };
    // let timeRangeValidator = timeRangeOverlapValidate(getTimeRangeFunc, getOtherTimeRangesFunc);
    // (<FormArray>this.createEventForm.get('timeRanges'))
    //     .push(this.formBuilder.group({
    //         startTime: [null, [Validators.required, timeRangeValidator]],
    //         endTime: [null, [Validators.required, timeRangeValidator]]
    //     }));
  }

  public removeTimeRange(i) {
    this.command.eventTimes.splice(i, 1);
    //(<FormArray>this.createEventForm.get('timeRanges')).removeAt(i);
  }

  public canDeactivate() {
    if (this.createEventForm.pristine) {
      return Observable.of(true);
    }

    let allowNavigate = new Subject<boolean>();

    this.modalService.showUnSaveWarning({ allowNavigate });

    return allowNavigate;
  }

  public descriptionChange() {
    this.descriptionArea.nativeElement.style.height =
      Math.min(this.descriptionArea.nativeElement.scrollHeight, 500) + 'px';
  }

  public resetTimezoneLabel() {
    // this.timezoneLabel = 'Timezone * (enter a city)';

    let timezoneFormatterDefault = localStorage.getItem(localStorageKeys.timezoneFormatterDefault);
    if (timezoneFormatterDefault && timezoneFormatterDefault !== 'undefined') {
      this.command.timezone = EventTimeService.getTimezoneCodeByFormatter(timezoneFormatterDefault);
      this.timezoneLabel = `Timezone (${this.command.timezone})`;
    } else {
      this.command.timezone = null; //EventTimeService.getTimezoneCode();
      this.manualSetTimezone = true;
      this.timezoneLabel = `Enter a city to set timezone *`;
    }
  }

  public citySelected() {
    if (this.timezoneAddress) {
      this.isLoadingTimezone = true;
      this.hubApiService
        .getTimezoneByLatlng(this.timezoneAddress.lat, this.timezoneAddress.lng)
        .finally(() => {
          this.isLoadingTimezone = false;
        })
        .subscribe((data) => {
          console.log(data);
          this.command.timezone = data.abbreviation;
          this.lastTimezone = this.command.timezone;
          this.hasSetTimezone = true;
          this.timezoneLabel = `Timezone (${this.command.timezone})`;
          this.dateTimeInput.toArray().forEach((x) => {
            x.timeFormatter = EventTimeService.getTimezoneFormatterByTimezone(this.command.timezone);
            x.timezone = this.command.timezone;
            x.setTime('start');
            x.setTime('end');
          });
        });
    }
  }

  private adjustForDaylightSaving(start, end, nextDayStart) {
    const formatter = EventTimeService.getTimezoneFormatterByTimezone(this.command.timezone);
    const startTimezone = EventTimeService.getTimezonecodeWithTimeSaving(start, formatter);
    const nextDayTimezone = EventTimeService.getTimezonecodeWithTimeSaving(nextDayStart, formatter);
    let thisEnd = end;
    let scrossDaylight = '';
    console.log('startTimezone, nextDayTimezone', startTimezone, nextDayTimezone);
    if (startTimezone !== nextDayTimezone) {
      if (nextDayTimezone.indexOf('ST') >= 0) {
        nextDayStart = EventTimeService.addHours(nextDayStart, 1);
        thisEnd = EventTimeService.addHours(thisEnd, 1);
        scrossDaylight = 'ST';
      } else {
        nextDayStart = EventTimeService.addHours(nextDayStart, -1);
        thisEnd = EventTimeService.addHours(thisEnd, -1);
        scrossDaylight = 'DT';
      }
    }
    return {
      startTime: nextDayStart,
      endTime: thisEnd,
      scrossDaylight,
    };
  }

  private resetFrequencyDropdown() {
    this.frequencyItems = [
      {
        text: 'Custom',
        value: 'custom',
        selected: true,
      },
      {
        text: 'Daily',
        value: 'daily',
      },
      {
        text: 'Weekly',
        value: 'weekly',
      },
      {
        text: 'Biweekly',
        value: 'biweekly',
      },
      {
        text: 'Monthly',
        value: 'monthly',
      },
    ];
  }

  private setEventTimes(start, end, eventTimes) {
    let startTime = start;
    let endTime = new Date(new Date(startTime).setUTCHours(new Date(end).getUTCHours()));
    endTime = new Date(endTime.setUTCMinutes(new Date(end).getUTCMinutes()));
    if (endTime < new Date(startTime)) {
      endTime = new Date(EventTimeService.addHours(endTime, 24));
    }
    eventTimes.push({
      startTime: startTime,
      endTime: endTime.toISOString(),
    });
  }

  private setDefaultTimeValue() {
    let endPlusHours = 0;
    switch (this.command.frequency) {
      case 'custom':
        endPlusHours = 24 + 3;
        break;
      case 'daily':
        endPlusHours = 24 * 7 + 3;
        break;
      case 'weekly':
        endPlusHours = 24 * 365 + 3;
        break;
    }
    if (!this.command.eventTimes[0].startTime && !this.command.eventTimes[0].endTime) {
      this.command.eventTimes = [
        {
          startTime: EventTimeService.getDefaultStartTime(this.command.timezone),
          endTime: EventTimeService.addHours(EventTimeService.getDefaultStartTime(this.command.timezone), endPlusHours),
        },
      ];
    } else if (this.command.eventTimes[0].startTime && !this.command.eventTimes[0].endTime) {
      this.command.eventTimes = [
        {
          startTime: this.command.eventTimes[0].startTime,
          endTime: EventTimeService.addHours(new Date(this.command.eventTimes[0].startTime), 24 * 7),
        },
      ];
    } else if (!this.command.eventTimes[0].startTime && this.command.eventTimes[0].endTime) {
      this.command.eventTimes = [
        {
          startTime: EventTimeService.addHours(new Date(this.command.eventTimes[0].startTime), 24 * 7 * -1),
          endTime: this.command.eventTimes[0].endTime,
        },
      ];
    }
  }

  private setImageUploaderLabel2() {
    const settingStorage = localStorage.getItem(localStorageKeys.allBackendSettings);
    if (settingStorage) {
      const settings = JSON.parse(settingStorage);
      this.visitorSettings = settings.customSettings as VisitorSettingsCommand;
      console.log(this.visitorSettings);
      if (this.visitorSettings.calendarStyle.event === 'square') {
        this.imageUploaderLabel2 = this.imageUploaderLabel2.replace('360px', '640px');
      }
      if (!isNullOrUndefined(this.visitorSettings.isAllowOnlineEvents)) {
        this.isAllowOnlineEvents = this.visitorSettings.isAllowOnlineEvents;
      }
    }
  }
}



// WEBPACK FOOTER //
// D:/Work/Development/vsts_build/_work/2/s/web/src/components/hub/manually.add/create.event/create.event.component.ts